import React, { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { shortenandHoverString, roundOff } from '../../../../Components/helper';
import { ImageCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDef } from '../../../../Components/BombiixAGGrid';
import { CostingActionsCell, CostingExpandAllHeader, CostingExpandCell, CostingHeader, CostingRefLinkCell, CommentCell, NodeCell, NumberCell, StatusCell } from './costing-todo-cells';
import type { CostingHeaderExtras } from './costing-todo-cells';
import { formatPercentageValue, setCurrency } from './costing-todo.helpers';
import { toIdString } from './calc';
import type { Ref } from './calc';
import { canEditDocumentCell, canEditRowCell, readCellValue, readCombinationPricePerUnit, readCombinationQuantity, readDynamicColumnValue, readLandedRow } from './costing-todo-editing';
import type { EditGates } from './costing-todo-editing';
import { resolveFactoryOptions } from './costing-todo-options';
import type { CostingTodoOptions, SelectOption } from './costing-todo-options';
import type { CostingGridRow, CostingInfoField, CostingRecord, CostingTemplate, TemplateColumn } from './store/Costing.type';

/* Column identifiers excluded from the trailing `flex: 1` pass — they keep their fixed widths. */
const STRUCTURAL_COL_IDS = new Set(['__expand', 'image', 'status', 'actions']);

/* Every other column gets `flex: 1`, and AG Grid ignores `width` once `flex` is set — so minWidth
   is the only lever on how narrow a column may become.

   `.ag-cell` spends 10px of padding plus a 1px border on each side, so a column has 22px less room
   than its width suggests. The input box is a fixed 130px (costing-todo-table.css), which puts the
   floor for a column holding one at 152.

   A cell pairing that box with a bracketed amount ('[ 10 ] % ($ 41.05)') also has to fit the
   bracket and its 4px margin — measured at 60px for the widest figure in the grid, carried to 70
   so a longer value or a wider currency symbol does not clip. */
const INPUT_CELL_MIN_WIDTH = 152;
const PAIRED_CELL_MIN_WIDTH = 225;

/* The cell types that render an input box, and so need the width above. `select`, `date`,
   `readonlyText` and `totalsFooter` draw their own controls and are left on the grid's default. */
const INPUT_CELL_TYPES = new Set(['text', 'currency', 'percent']);

/* Template aliases that BM-9220 makes editable, mapped to the calc engine's field names. Anything
   not listed here stays read-only, including every derived figure. */
const EDITABLE_DEFAULT_ALIASES: Record<string, string> = {
  dutyPercentage: 'dutyPercentage',
  tariffCostPercentage: 'tariffCostPercentage',
  freightPercentage: 'freightPercentage',
  freightValue: 'freightValue',
};

/**
 * Casts a grid colId to AG Grid's `field` type. The editable cells read `colDef.field` to name the
 * value they changed, but our colIds are column identifiers rather than paths into the row object,
 * which is what AG Grid's generic `field` type expects.
 */
const asField = (colId: string): NonNullable<BombiixColumnDef<CostingGridRow>['field']> => colId as NonNullable<BombiixColumnDef<CostingGridRow>['field']>;

/* The two top-family columns denominated in the distribution centre's currency — the same pair the
   detail page's landed table renders. Every other top column is a bare figure. */
const CONVERTED_TOP_ALIASES = new Set(['pricePerUnitIncoConverted', 'totalIncoConverted']);

/* Default-family columns the engine derives from a rate rather than accepting as input. They render
   as disabled controls showing the amount, with the rate in brackets where the template stores one. */
const ROYALTY_FEE_ALIASES = new Set(['licenseRoyaltyFee', 'likenessRoyaltyFee']);

/* Bottom-family columns that are ratios rather than money, shown with a `%` suffix and no bracket. */
const RATIO_BOTTOM_ALIASES = new Set(['marginPerUnit', 'retailMarginPerUnit']);

/* The trailing bottom-family columns priced in the SUB-BRAND's currency rather than the
   distribution centre's — the same pair the detail page's landed table treats this way. */
const RETAIL_AMOUNT_ALIASES = new Set(['estimatedRetailValue', 'estimatedProfitPerUnit']);

const EDITABLE_BOTTOM_ALIASES: Record<string, string> = {
  sellingPriceGross: 'sellingPriceGross',
  vat: 'vat',
  customerDiscount: 'customerDiscount',
  estimatedRetailValue: 'estimatedRetailValue',
};

export interface CostingColumnsParams {
  selectedColumns: string[];
  t: TranslateFn;
  tMyTodo: TranslateFn;
  getCostingInfoLabel: (position: number, fallback: string) => string;
  getDynamicCostingValue: (cost: CostingRecord | undefined, item: CostingInfoField) => string;
  onSortColumn: (propertyName: string) => void;
  sortOrder: string;
  sortPropertyName: string;
  expandedRowIndex: Array<number | null>;
  expandMoreData: (index: number) => void;
  expandAll: () => void;
  costingData: CostingRecord[];
  isCostingInfoActive: boolean;
  dynamicCostingInfo: CostingInfoField[];
  topColumnsArr: TemplateColumn[] | undefined;
  defaultColumnsArr: TemplateColumn[] | undefined;
  bottomColumnsArr: TemplateColumn[] | undefined;
  activeCostingTemplateDetailes: CostingTemplate | undefined;
  isSupplierLoggedIn: boolean;
  MasterAddEditAccess: boolean;
  subBrandAccess: boolean;
  /** Sub-brand master list, for the currency the retail columns are denominated in. */
  subBrandList?: Array<{ _id?: string; name?: string; currency?: string }> | undefined;
  handleEdit: (item: CostingRecord) => void;
  /** Gates that disable every editable cell regardless of column (BM-9220). */
  editGates: EditGates;
  /** Dropdown option sources for the editable columns. */
  editOptions: CostingTodoOptions;
}

/**
 * Builds the Costing TODO column definitions. The set is fully dynamic: the active costing
 * template supplies the top/default/bottom/landedInfo families and the costingInfo labels,
 * while the persisted column chooser decides which of them are shown.
 */
export function useCostingTodoColumns(params: CostingColumnsParams): BombiixColumnDef<CostingGridRow>[] {
  const {
    selectedColumns,
    t,
    tMyTodo,
    getCostingInfoLabel,
    getDynamicCostingValue,
    onSortColumn,
    sortOrder,
    sortPropertyName,
    expandedRowIndex,
    expandMoreData,
    expandAll,
    costingData,
    isCostingInfoActive,
    dynamicCostingInfo,
    topColumnsArr,
    defaultColumnsArr,
    bottomColumnsArr,
    activeCostingTemplateDetailes,
    isSupplierLoggedIn,
    MasterAddEditAccess,
    subBrandAccess,
    subBrandList,
    handleEdit,
    editGates,
    editOptions,
  } = params;

  /* Only the ROW COUNT is needed here (the expand-all chevron compares it against the expanded
     list), and depending on the array itself rebuilt every column definition on every cell edit —
     `setInitState` hands back a new `costingData` array each time. AG Grid treats new definitions
     as a column change: it re-applies them, and with `maintainColumnOrder` at its default of false
     that resets column order and disturbs the horizontal scroll. Editing a value must not look
     like a column change. */
  const costingRowCount = costingData?.length ?? 0;

  return useMemo<BombiixColumnDef<CostingGridRow>[]>(() => {
    const cols: BombiixColumnDef<CostingGridRow>[] = [];
    /**
     * The currency the landed columns are denominated in: the distribution centre's, which the list
     * endpoint lifts from `landedInformations[0].landedExchangeRateInformation`.
     *
     * A costing with no distribution centre carries no such subtree, so the engine applies no
     * conversion and the figures stay in the supplier's currency — the product currency is the
     * correct symbol for those rows, not a blank one. Without the fallback they rendered an empty
     * prefix slot next to a visible divider, which reads as a broken cell.
     */
    const cur = (cost: CostingRecord | undefined) => setCurrency(cost?.landedExchangeRateInformation?.companyCurrency || cost?.productInfo?.currency);

    /**
     * The currency the retail columns are denominated in: the costing's sub-brand has its own,
     * distinct from the distribution centre's. Falls back to the distribution centre currency when
     * the sub-brand has none, mirroring the detail page's landed table.
     */
    const retailCur = (cost: CostingRecord | undefined) => {
      const subBrandId = toIdString((cost?.subBrandId ?? null) as Ref | string | null);
      const subBrandCurrency = subBrandId ? subBrandList?.find((subBrand) => toIdString((subBrand?._id ?? null) as Ref | string | null) === subBrandId)?.currency : undefined;
      return subBrandCurrency ? setCurrency(subBrandCurrency) : cur(cost);
    };

    /** Per-row currency props for a money cell, so each costing shows its own symbol. */
    const currencyOf = (resolve: (cost: CostingRecord | undefined) => string) => (row: CostingGridRow) => ({ currencySymbol: resolve(row.cost) });

    /** Read-only column driven by an accessor; typed once so every inline arrow below infers. */
    const node = (accessor: (row: CostingGridRow) => React.ReactNode): Pick<BombiixColumnDef<CostingGridRow>, 'cellRenderer' | 'cellProps'> => ({
      cellRenderer: NodeCell,
      cellProps: { accessor },
    });

    const header = (label: React.ReactNode, extra: CostingHeaderExtras = {}): Pick<BombiixColumnDef<CostingGridRow>, 'headerComponent' | 'headerComponentParams'> => ({
      headerComponent: CostingHeader,
      headerComponentParams: { label, sortOrder, sortPropertyName, ...extra },
    });

    /**
     * A dropdown cell for a value stored once per costing. Editable on the parent row only — a
     * document field edited on a child row would silently change every sibling combination.
     */
    const documentSelect = (
      colId: string,
      options: SelectOption[],
      readValue: (row: CostingGridRow) => string | undefined,
      extra: Partial<BombiixColumnDef<CostingGridRow>> = {},
    ): Partial<BombiixColumnDef<CostingGridRow>> => ({
      field: asField(colId),
      cellType: 'select',
      cellProps: { options, isClearable: false },
      valueGetter: (params) => (params.data ? readCellValue(params.data, colId, (row) => readValue(row) ?? null) : null),
      getCellDisabled: (row) => !canEditDocumentCell(row, editGates),
      ...extra,
    });

    /** A number cell for a value stored once per costing. Parent row only, as above. */
    const documentNumber = (colId: string, readValue: (row: CostingGridRow) => string | undefined): Partial<BombiixColumnDef<CostingGridRow>> => ({
      field: asField(colId),
      cellType: 'text',
      cellProps: { type: 'number' },
      valueGetter: (params) => (params.data ? readCellValue(params.data, colId, (row) => readValue(row) ?? '') : ''),
      getCellDisabled: (row) => !canEditDocumentCell(row, editGates),
    });

    /**
     * A number cell for a value stored per colourway/size/length combination. Editable on the parent
     * row when the costing has a single combination, and on the child rows once expanded.
     */
    const rowNumber = (colId: string, field: string, readValue: (row: CostingGridRow) => string | number | undefined): Partial<BombiixColumnDef<CostingGridRow>> => ({
      field: asField(colId),
      cellType: 'text',
      cellProps: { type: 'number' },
      valueGetter: (params) => (params.data ? readCellValue(params.data, colId, (row) => readValue(row) ?? '') : ''),
      getCellDisabled: (row) => !canEditRowCell(row, field, editGates),
      getCellHidden: (row) => row.__rowType === 'parent' && row.hasExpanded,
    });

    /**
     * Resolves a dropdown's selected id.
     * The list API returns these references populated as `{ _id, name }`, but an inline edit writes
     * a plain id string — so reading `._id` alone blanks the cell after the first change.
     */
    const selectedId = (value: unknown): string | undefined => toIdString(value as Ref | string | null) ?? undefined;

    /**
     * Appends the percentage marker only when the template's own label does not already carry one —
     * display names like 'Duty %' otherwise rendered as 'Duty % (%)'.
     */
    const percentHeader = (label: string): string => (label.includes('%') ? label : `${label} (%)`);

    /** Reads a landed input off the combination a grid row addresses. */
    const landedInput = (alias: string) => (row: CostingGridRow) => {
      const landed = readLandedRow(row) as Record<string, unknown> | undefined;
      const raw = landed?.[alias];
      return raw == null ? '' : String(raw);
    };

    /** Reads the amount a percentage column's rate derives, e.g. `dutyPercentageCalculatedValue`. */
    const landedCalc = (alias: string) => (row: CostingGridRow) => {
      const landed = readLandedRow(row) as Record<string, unknown> | undefined;
      const raw = landed?.[`${alias}CalculatedValue`];
      return raw == null ? undefined : (raw as string | number);
    };

    /** Reads the amount a dynamic template percentage column derives. */
    const dynamicCalc = (columnId: string | undefined, columnKey: string | undefined) => (row: CostingGridRow) => {
      const landed = readLandedRow(row);
      return landed?.landedInfo?.find((entry) => entry?._id === columnId && entry?.key === columnKey)?.calculatedValue;
    };

    /**
     * An editable percentage cell: the rate the user types, a `%` suffix inside the field, and the
     * amount that rate derives shown alongside — the same shape the costing detail page renders.
     */
    const percentRow = (
      colId: string,
      field: string,
      readValue: (row: CostingGridRow) => string | number | undefined,
      calcAccessor?: (row: CostingGridRow) => string | number | undefined,
    ): Partial<BombiixColumnDef<CostingGridRow>> => ({
      ...rowNumber(colId, field, readValue),
      cellType: 'percent',
      cellProps: { calcAccessor },
      getCellProps: (row) => ({ currencySymbol: cur(row.cost) }),
    });

    /**
     * Reads a derived landed figure off the combination a grid row addresses, rounded the way the
     * read-only columns rendered it. An expanded parent aggregates several combinations, so it
     * reports 'Multiple' rather than any single one of them.
     */
    const derivedValue =
      (pick: (landed: Record<string, unknown> | undefined) => unknown) =>
      (row: CostingGridRow): string => {
        if (row.__rowType === 'parent' && row.hasExpanded) return 'Multiple';
        const landed = (row.__rowType === 'child' ? row.expData?.landedData : row.rowOneData?.landedData) as Record<string, unknown> | undefined;
        return String(roundOff(pick(landed) ?? '0', 2, true) || '0');
      };

    /**
     * A derived figure shown as a disabled control, so a landed row reads as one continuous strip
     * of inputs rather than alternating boxes and bare text. Disabled rather than read-only on
     * purpose: getCellDisabled keeps the real cell renderer and greys it, whereas getCellReadOnly
     * swaps in plain text — which is what an expanded parent falls back to, having no single
     * figure to put in a box.
     */
    const derived = (
      cellType: 'currency' | 'percent' | 'text',
      pick: (landed: Record<string, unknown> | undefined) => unknown,
      options: { currencyFor?: (row: CostingGridRow) => string; calcAccessor?: (row: CostingGridRow) => string | number | undefined } = {},
    ): Partial<BombiixColumnDef<CostingGridRow>> => {
      const read = derivedValue(pick);
      const resolveCurrency = options.currencyFor ?? ((row: CostingGridRow) => cur(row.cost));
      return {
        cellType,
        cellProps: { type: 'number', calcAccessor: options.calcAccessor },
        valueGetter: (params) => (params.data ? read(params.data) : ''),
        getCellDisabled: () => true,
        getCellReadOnly: (row) => row.__rowType === 'parent' && row.hasExpanded,
        getCellProps: (row) => ({ currencySymbol: resolveCurrency(row) }),
      };
    };

    cols.push({
      colId: '__expand',
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      suppressHeaderMenuButton: true,
      /* 40px column minus 10px padding each side leaves 20px — the chevron is centred rather than
         left-hugging so it lines up with the header toggle above it. */
      cellClass: 'cag-cell-center',
      headerClass: 'cag-header-center',
      /* Shared chevron affordance, matching the BulkEdit Measurements grid. */
      cellRenderer: CostingExpandCell,
      cellProps: {
        getExpanded: (r?: CostingGridRow) => (r ? expandedRowIndex?.includes(r.parentIndex) : false),
        onToggle: (r?: CostingGridRow) => {
          if (r) expandMoreData(r.parentIndex);
        },
      },
      headerComponent: CostingExpandAllHeader,
      headerComponentParams: {
        allExpanded: costingRowCount > 0 && costingRowCount === expandedRowIndex?.length,
        onToggleAll: () => expandAll(),
      },
    });

    cols.push({
      colId: 'image',
      headerName: 'Image',
      width: 59,
      minWidth: 59,
      maxWidth: 59,
      pinned: 'left',
      sortable: false,
      suppressHeaderMenuButton: true,
      /* `.ag-cell` is flex with 10px side padding, so the 32px tile would sit flush-left in the
         59px column while the header label did not. The shared centring helpers align both. */
      cellClass: 'cag-cell-center',
      headerClass: 'cag-header-center',
      /* Shared thumbnail cell (hover-zoom + placeholder tile), matching the Measurements grid. It
         reads the image path from the column value, so the parent row's stored path is fed here
         and child rows are hidden rather than handed an empty path. */
      cellRenderer: ImageCell,
      valueGetter: (params) => (params.data?.__rowType === 'parent' ? (params.data.cost?.image ?? '') : ''),
      getCellHidden: (row) => row.__rowType !== 'parent',
    });

    cols.push({
      colId: 'number',
      headerName: 'Product Number',
      width: 180,
      pinned: 'left',
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: NumberCell,
    });

    if (selectedColumns?.includes('reference'))
      cols.push({
        colId: 'reference',
        width: 150,
        pinned: 'left',
        sortable: false,
        ...header(getCostingInfoLabel(0, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COSTING_REFERENCE')), {
          sortKey: 'reference',
          onSort: onSortColumn,
        }),
        /* Underlined link opening the edit flow the actions column's edit button used to own. */
        cellRenderer: CostingRefLinkCell,
        cellProps: { onEdit: handleEdit, canEdit: MasterAddEditAccess },
      });

    if (selectedColumns?.includes('supplier'))
      cols.push({
        colId: 'supplier',
        width: 224,
        sortable: false,
        ...header(getCostingInfoLabel(1, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SUPLIER'))),
        ...documentSelect('supplier', [], (r) => selectedId(r.cost?.productInfo?.supplierId)),
        /* A costing may only use the suppliers assigned to its own development order, so the options
           resolve per row rather than from a shared tenant-wide list. */
        getCellProps: (r) => ({ options: editOptions.supplierOptionsByDevOrder[String(r.cost?.devOrderId ?? '')] ?? [] }),
      });

    if (selectedColumns?.includes('factory'))
      cols.push({
        colId: 'factory',
        width: 224,
        sortable: false,
        ...header(getCostingInfoLabel(2, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.FACTORY'))),
        ...documentSelect('factory', [], (r) => selectedId(r.cost?.productInfo?.factoryId)),
        /* Two filters, both per row: the factory must belong to the row's selected supplier AND have
           been chosen for this product at creation. A product with no factories selected therefore
           offers none, rather than every factory the supplier happens to own. */
        getCellProps: (r) => ({ options: resolveFactoryOptions(r, editOptions.factoryOptionsBySupplier) }),
      });

    if (selectedColumns?.includes('currency'))
      cols.push({
        colId: 'currency',
        width: 130,
        sortable: false,
        ...header(getCostingInfoLabel(4, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.CURRENCY')), { sortKey: 'currency', onSort: onSortColumn }),
        ...node((r) => shortenandHoverString(r.cost?.productInfo?.currency || '--', 20)),
      });

    if (selectedColumns?.includes('moqNeeded'))
      cols.push({
        colId: 'moqNeeded',
        width: 130,
        sortable: false,
        ...header(getCostingInfoLabel(5, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.MOQ_NEEDED'))),
        ...documentNumber('moqNeeded', (r) => r.cost?.productInfo?.moqNeeded?.toString()),
      });

    if (selectedColumns?.includes('targetCost'))
      cols.push({
        colId: 'targetCost',
        width: 130,
        sortable: false,
        ...header(getCostingInfoLabel(6, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.TARGET_COST')), { sortKey: 'targetCost', onSort: onSortColumn }),
        ...documentNumber('targetCost', (r) => (r.cost?.productInfo?.targetCost ?? r.cost?.targetCost)?.toString()),
        cellType: 'currency',
        getCellProps: currencyOf(cur),
      });

    if (selectedColumns?.includes('incoterm'))
      cols.push({
        colId: 'incoterm',
        width: 130,
        sortable: false,
        ...header(getCostingInfoLabel(7, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.INCOTERMS')), { sortKey: 'incoterms', onSort: onSortColumn }),
        ...documentSelect('incoterm', editOptions.incotermOptions, (r) => selectedId(r.cost?.productInfo?.incoterms)),
      });

    if (selectedColumns?.includes('shipmentMethod'))
      cols.push({
        colId: 'shipmentMethod',
        width: 130,
        sortable: false,
        ...header(getCostingInfoLabel(8, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SHIPMENT_METHOD')), {
          sortKey: 'shipmentMethod',
          onSort: onSortColumn,
        }),
        ...documentSelect('shipmentMethod', editOptions.shipmentMethodOptions, (r) => r.cost?.productInfo?.shipmentMethod),
      });

    if (selectedColumns?.includes('sampleRef'))
      cols.push({
        colId: 'sampleRef',
        width: 224,
        sortable: false,
        ...header(getCostingInfoLabel(9, t('costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SAMPLE_REF'))),
        ...documentSelect('sampleRef', [], (r) => selectedId(r.cost?.sampleRef), { cellProps: { options: [], isClearable: true } }),
        /* A sample belongs to one product, so the options resolve per row rather than from a shared
           tenant-wide list — the same shape the supplier column uses. */
        getCellProps: (r) => ({ options: editOptions.sampleRefOptionsByDevOrder[String(r.cost?.devOrderId ?? '')] ?? [], isClearable: true }),
      });

    if (isCostingInfoActive)
      dynamicCostingInfo?.forEach((dynCol) => {
        if (!selectedColumns?.includes(`costingInfo_${dynCol?._id}`)) return;
        cols.push({
          colId: `costingInfo_${dynCol?._id}`,
          width: 130,
          sortable: false,
          ...header(dynCol?.aliasDisplayName || dynCol?.title),
          ...node((r) => (r.__rowType === 'child' ? '' : shortenandHoverString(getDynamicCostingValue(r.cost, dynCol), 20))),
        });
      });

    if (selectedColumns?.includes('developmentOrder'))
      cols.push({
        colId: 'developmentOrder',
        width: 150,
        sortable: false,
        ...header('Development Order'),
        ...node((r) => (r.__rowType === 'child' ? '' : shortenandHoverString(r.cost?.devOrderId || '--', 20))),
      });

    cols.push({
      colId: 'costingType',
      width: 130,
      sortable: false,
      ...header(t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COSTING_TYPE')),
      ...node((r) => (r.__rowType === 'child' ? '' : shortenandHoverString(r.cost?.isAverageCosting ? tMyTodo('MY_TODO.LIST.AVERAGE') : tMyTodo('MY_TODO.LIST.DETAILED'), 20))),
    });

    if (selectedColumns?.includes('colourway'))
      cols.push({
        colId: 'colourway',
        width: 224,
        sortable: false,
        ...header(t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COLOUR_WAY')),
        ...node((r) =>
          r.__rowType === 'child'
            ? shortenandHoverString(r.expData?.colourway?.name || '--', 20)
            : shortenandHoverString(
                (r.cost?.costColourways?.length ?? 0) > 1 ? 'Multiple' : r.cost?.costColourways?.length === 1 ? r.cost?.costColourways?.[0]?.name : '--',
                15,
              ),
        ),
      });

    if (selectedColumns?.includes('size'))
      cols.push({
        colId: 'size',
        width: 224,
        sortable: false,
        ...header('Size'),
        ...node((r) =>
          r.__rowType === 'child'
            ? shortenandHoverString(r.expData?.size || '--', 20)
            : shortenandHoverString((r.cost?.costSizes?.length ?? 0) > 1 ? 'Multiple' : r.cost?.costSizes?.length === 1 ? r.cost?.costSizes?.[0] : '--', 20),
        ),
      });

    if (selectedColumns?.includes('length'))
      cols.push({
        colId: 'length',
        width: 130,
        sortable: false,
        ...header('Length'),
        ...node((r) =>
          r.__rowType === 'child'
            ? shortenandHoverString(r.expData?.length?.name || '--', 20)
            : shortenandHoverString((r.cost?.costLengths?.length ?? 0) > 1 ? 'Multiple' : r.cost?.costLengths?.length === 1 ? r.cost?.costLengths?.[0]?.name : '--', 20),
        ),
      });

    if (selectedColumns?.includes('quantity'))
      cols.push({
        colId: 'quantity',
        width: 130,
        sortable: false,
        ...header(t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.QUANTITY')),
        /* Read from `combinations`, not the landed row — see readCombinationQuantity. The landed
           figure is grand-total derived and stays stale for a combination the filter excludes. */
        ...rowNumber('quantity', 'quantity', readCombinationQuantity),
      });

    if (selectedColumns?.includes('pricePerUnit'))
      cols.push({
        colId: 'pricePerUnit',
        width: 130,
        sortable: false,
        ...header(t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.PRICE_PER_UNIT')),
        /* The stored per-combination price, read from the same path the edit writes. Editable on
           average costing only — on a detailed costing it is rolled up from the BOM, so
           canEditRowCell disables it there.

           A detailed costing keeps its quantity rows on `quantities`, which carry no price at all —
           the price lives in the BOM — so the projected combination reports ''. Showing 0 rather
           than an empty box makes the disabled cell read as a figure instead of a missing input. */
        ...rowNumber('pricePerUnit', 'pricePerUnit', (r) => (r.cost?.isDetailedCosting ? '0' : readCombinationPricePerUnit(r))),
        cellType: 'currency',
        getCellProps: currencyOf((cost) => setCurrency(cost?.productInfo?.currency)),
      });

    if (selectedColumns?.includes('pricePerUnitIncoterm'))
      cols.push({
        colId: 'pricePerUnitIncoterm',
        width: 130,
        sortable: false,
        ...header(`${t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.PRICE_PER_UNIT')} (Incoterms)`),
        /* Derived: this is the incoterm roll-up produced by the engine, not an input, so it renders
           disabled. The editable per-combination price is the Price Per Unit column above. Priced in
           the supplier's currency, unlike the landed columns which use the distribution centre's. */
        ...derived('currency', (landed) => landed?.pricePerUnitInco, { currencyFor: (r) => setCurrency(r.cost?.productInfo?.currency) }),
      });

    if (selectedColumns?.includes('totalIncoterm'))
      cols.push({
        colId: 'totalIncoterm',
        width: 130,
        sortable: false,
        ...header('Total (Incoterms)'),
        ...derived('currency', (landed) => landed?.totalInco, { currencyFor: (r) => setCurrency(r.cost?.productInfo?.currency) }),
      });

    if (!isSupplierLoggedIn) {
      if (Array.isArray(topColumnsArr))
        topColumnsArr.forEach((topCol) => {
          cols.push({
            colId: `top_${topCol?.aliasName}`,
            width: 130,
            sortable: false,
            ...header(topCol?.aliasDisplayName || topCol?.title),
            /* The converted pair is money in the distribution centre's currency — the only two top
               columns the detail page renders, and the only two carrying a symbol here. */
            ...derived(CONVERTED_TOP_ALIASES.has(topCol?.aliasName) ? 'currency' : 'text', (landed) => landed?.[topCol?.aliasName]),
          });
        });

      if (Array.isArray(activeCostingTemplateDetailes?.landedInfo))
        activeCostingTemplateDetailes?.landedInfo
          ?.filter((vl) => vl?.isActive)
          ?.forEach((landedCol) => {
            const isPercentage = Boolean(landedCol?.type?.includes('percentage'));
            /* CostingTODOTable reads these two off the colDef to route the edit back to the right
               template entry, so they must survive alongside the cell's own props. */
            const routing = { dynamicColumnId: landedCol?._id, dynamicColumnKey: landedCol?.type };
            cols.push({
              colId: `landed_${landedCol?._id}`,
              width: 130,
              ...(isPercentage ? { minWidth: PAIRED_CELL_MIN_WIDTH } : {}),
              sortable: false,
              ...header(isPercentage ? percentHeader(landedCol?.aliasDisplayName || landedCol?.title || '') : landedCol?.aliasDisplayName || landedCol?.title),
              /* Editable: the cell shows the rate the user types, with the amount that rate derives
                 beside it. The entry is updated in place — the template owns which columns exist
                 and their identity fields never change. */
              ...rowNumber(`landed_${landedCol?._id}`, 'dutyPercentage', (r) => readDynamicColumnValue(r, landedCol?._id, landedCol?.type)),
              ...(isPercentage
                ? {
                    cellType: 'percent' as const,
                    cellProps: { ...routing, calcAccessor: dynamicCalc(landedCol?._id, landedCol?.type) },
                    getCellProps: (row: CostingGridRow) => ({ currencySymbol: cur(row.cost) }),
                  }
                : { cellType: 'currency' as const, cellProps: { ...routing, type: 'number' }, getCellProps: (row: CostingGridRow) => ({ currencySymbol: cur(row.cost) }) }),
            });
          });

      if (Array.isArray(defaultColumnsArr) && !isEmpty(defaultColumnsArr))
        defaultColumnsArr.forEach((colDefault) => {
          if (!colDefault?.isActive) return;
          const editableField = EDITABLE_DEFAULT_ALIASES[colDefault?.aliasName];
          const alias = colDefault?.aliasName;
          if (editableField) {
            const isPercentage = colDefault?.type === 'percentage';
            cols.push({
              colId: `default_${alias}`,
              width: 130,
              ...(isPercentage ? { minWidth: PAIRED_CELL_MIN_WIDTH } : {}),
              sortable: false,
              /* Percentage columns carry a (%) marker because the cell shows the RATE the user
                 types, with the amount it derives rendered beside it. */
              ...header(isPercentage ? percentHeader(colDefault?.aliasDisplayName || colDefault?.title || '') : colDefault?.aliasDisplayName || colDefault?.title),
              ...(isPercentage
                ? percentRow(`default_${alias}`, editableField, landedInput(alias), landedCalc(alias))
                : { ...rowNumber(`default_${alias}`, editableField, landedInput(alias)), cellType: 'currency' as const, getCellProps: currencyOf(cur) }),
              /* Freight percentage only applies while the row's freight input is switched on, which
                 is where the detail page disables it too. */
              ...(alias === 'freightPercentage'
                ? {
                    getCellDisabled: (row: CostingGridRow) =>
                      !canEditRowCell(row, editableField, editGates) || !(readLandedRow(row) as Record<string, unknown> | undefined)?.freightInputEnabled,
                  }
                : {}),
            });
            return;
          }
          /* Royalty fees are money the engine derives from a stored rate, so they show the amount
             with the rate in brackets — the same pairing the detail page renders. */
          const isRoyaltyFee = ROYALTY_FEE_ALIASES.has(alias);
          cols.push({
            colId: `default_${alias}`,
            width: 130,
            ...(isRoyaltyFee ? { minWidth: PAIRED_CELL_MIN_WIDTH } : {}),
            sortable: false,
            ...header(colDefault?.aliasDisplayName || colDefault?.title),
            ...derived('currency', (landed) => landed?.[`${alias}CalculatedValue`] ?? landed?.[alias]),
            ...(isRoyaltyFee
              ? {
                  cellProps: {
                    type: 'number',
                    noteAccessor: (row: CostingGridRow) => formatPercentageValue((readLandedRow(row) as Record<string, unknown> | undefined)?.[alias] ?? '0'),
                  },
                }
              : {}),
          });
        });

      if (Array.isArray(bottomColumnsArr))
        bottomColumnsArr.forEach((bottomCol) => {
          if (!bottomCol?.isActive) return;
          const editableBottomField = EDITABLE_BOTTOM_ALIASES[bottomCol?.aliasName];
          const bottomAlias = bottomCol?.aliasName;
          if (editableBottomField) {
            const isPercentage = ['vat', 'customerDiscount'].includes(bottomAlias);
            cols.push({
              colId: `bottom_${bottomAlias}`,
              width: 130,
              ...(isPercentage ? { minWidth: PAIRED_CELL_MIN_WIDTH } : {}),
              sortable: false,
              ...header(isPercentage ? percentHeader(bottomCol?.aliasDisplayName || bottomCol?.title || '') : bottomCol?.aliasDisplayName || bottomCol?.title),
              ...(isPercentage
                ? percentRow(`bottom_${bottomAlias}`, editableBottomField, landedInput(bottomAlias), landedCalc(bottomAlias))
                : {
                    ...rowNumber(`bottom_${bottomAlias}`, editableBottomField, landedInput(bottomAlias)),
                    cellType: 'currency' as const,
                    getCellProps: currencyOf(RETAIL_AMOUNT_ALIASES.has(bottomAlias) ? retailCur : cur),
                  }),
            });
            return;
          }
          /* Margins are ratios, so they keep a `%` suffix and no bracketed amount. Everything else
             left in this family is money the engine derived. */
          const isRatio = RATIO_BOTTOM_ALIASES.has(bottomAlias);
          cols.push({
            colId: `bottom_${bottomAlias}`,
            width: 130,
            sortable: false,
            ...header(bottomCol?.aliasDisplayName || bottomCol?.title),
            ...derived(isRatio ? 'percent' : 'currency', (landed) => landed?.[bottomAlias], RETAIL_AMOUNT_ALIASES.has(bottomAlias) ? { currencyFor: (r) => retailCur(r.cost) } : {}),
          });
        });
    }

    cols.push({
      colId: 'comment',
      headerName: t('COSTING_TO_DO_LIST.MOST_RECENT_COMMENT'),
      width: 224,
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: CommentCell,
    });

    /* Wide enough for the longest status the pill can carry. `.costing-status-pill` already hugs its
       text (inline-flex + nowrap), so the pill sizes itself — but it cannot grow past its cell, and
       at 110px "Partially Complete" filled the column edge to edge and read as broken. Short
       statuses keep the pill's 100px min-width and stay centred. */
    cols.push({
      colId: 'status',
      headerName: 'Status',
      width: 152,
      pinned: 'right',
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: StatusCell,
    });

    /* Holds one 24px info icon and no header label, so it only needs room for the icon plus the
       cell's own 22px of padding and border — the previous 110px was leftover from when this column
       carried the edit pencil as well.

       `minWidth` is required, not decorative: BombiixAGGrid defaults every column to `minWidth: 120`
       and AG Grid clamps `width` up to it, so a `width` below 120 is ignored outright unless the
       column declares its own floor. */
    cols.push({
      colId: 'actions',
      headerName: '',
      width: 40,
      minWidth: 40,
      pinned: 'right',
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: CostingActionsCell,
      cellProps: {
        t,
      },
    });

    /**
     * Resolves the placeholder an editable cell should carry.
     *
     * The shared cells fall back to `colDef.headerName` and then to a literal `'Text Box'` /
     * `'Dropdown'` / `'Date'`. Every column here renders its label through `headerComponent` rather
     * than `headerName`, so that last-resort fallback is what actually showed — a Duty % cell read
     * "Text Box" and the Supplier cell read "Dropdown". Numeric cells take no placeholder at all,
     * matching the costing detail page; dropdowns take their own column label.
     * @returns the placeholder to inject, or undefined to leave the cell's own default alone
     */
    const placeholderFor = (column: BombiixColumnDef<CostingGridRow>): string | undefined => {
      if (!column.cellType || column.cellType === 'readonlyText' || column.cellType === 'totalsFooter') return undefined;
      if (column.cellType === 'select' || column.cellType === 'date') {
        const label = (column.headerComponentParams as { label?: unknown } | undefined)?.label;
        return typeof label === 'string' ? label : undefined;
      }
      return '';
    };

    return cols.map((column) => {
      const placeholder = placeholderFor(column);
      /* An explicit cellProps.placeholder still wins — this only fills the gap. */
      const labelled = placeholder === undefined ? column : { ...column, cellProps: { placeholder, ...(column.cellProps ?? {}) } };
      const lockedColumn = labelled.pinned ? { ...labelled, suppressMovable: true } : labelled;
      if (STRUCTURAL_COL_IDS.has(lockedColumn.colId ?? '')) return lockedColumn;
      /* minWidth sits before the spread so a column declaring its own keeps it. */
      return INPUT_CELL_TYPES.has(lockedColumn.cellType ?? '') ? { flex: 1, minWidth: INPUT_CELL_MIN_WIDTH, ...lockedColumn } : { flex: 1, ...lockedColumn };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedColumns,
    activeCostingTemplateDetailes,
    topColumnsArr,
    defaultColumnsArr,
    bottomColumnsArr,
    dynamicCostingInfo,
    isCostingInfoActive,
    isSupplierLoggedIn,
    sortOrder,
    sortPropertyName,
    expandedRowIndex,
    costingRowCount,
    MasterAddEditAccess,
    subBrandAccess,
    subBrandList,
    /* Supplier and factory options are fetched per development order and land AFTER the first
       render. Without this the dropdowns kept the closure built over the empty option lists, so a
       row with a stored supplier showed the bare "Supplier" placeholder until some unrelated change
       — expanding a row, which moves `expandedRowIndex` — happened to rebuild the columns.
       `useCostingTodoOptions` memoises the object it returns, so this stays stable between loads. */
    editOptions,
    /* Every editable column closes over this in `getCellDisabled`, and `canEdit` is
       `MasterAddEditAccess && !isSaving`. `MasterAddEditAccess` alone is not enough: it never
       changes during a save, so when `isSaving` flipped the columns were not rebuilt and every cell
       kept a closure over the pre-save gates, staying editable for the whole in-flight request.
       CostingTODOTable memoises this object on those same three inputs, so it is referentially
       stable between renders and does not reintroduce the per-keystroke column churn. */
    editGates,
  ]);
}

export default useCostingTodoColumns;
